import { useSellerDebts } from '@/lib/consoleQueries';
import { Icon } from '@/components/icons/Icon';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { AdminError } from '@/features/admin/AdminError';

/**
 * What this pharmacy owes, and how to clear it.
 *
 * ── §4.11: A DEDUCTION A SELLER CANNOT TRACE IS WORSE THAN THE DEDUCTION ────
 *
 * Every line links back to the refund and the order that caused it. A
 * pharmacist who cannot trace a deduction assumes the worst about the platform
 * — and at most marketplaces they would be right to.
 *
 * ── THE TONE IS DELIBERATE ──────────────────────────────────────────────────
 *
 * A pharmacist reading this has usually done nothing wrong: a buyer was
 * refunded after the pharmacy had already been paid, which is the system
 * working as designed. So the copy explains rather than scolds, and the bank
 * details are on the same screen as the balance — half the reason debts age is
 * that nobody knows who to pay, so nobody pays.
 */
export function MoneyScreen() {
  const { data, isLoading, isError, error, refetch } = useSellerDebts();

  if (isError) {
    return <AdminError title="We couldn't load your account" error={error} onRetry={() => refetch()} />;
  }

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-[900px] space-y-4 px-4 py-5">
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-32 w-full" radius="card" />
      </div>
    );
  }

  const clear = data.statement.outstanding_kobo <= 0;

  return (
    <div className="mx-auto w-full max-w-[900px] px-4 py-5 xl:px-8">
      <header className="mb-4">
        <h1 className="m-0 font-display text-[24px] font-bold tracking-[-0.02em]">Money</h1>
      </header>

      {data.suspendedForDebt ? (
        <div
          data-testid="suspended-banner"
          className="mb-4 flex items-start gap-2.5 rounded-card border border-danger bg-danger-tint p-4"
        >
          <Icon name="alert" size={18} className="mt-0.5 shrink-0 text-danger" />
          <p className="m-0 text-[13.5px] font-medium leading-relaxed text-ink">
            Your listings are paused until this is settled. Send the transfer, tell us the
            reference, and we restore them the same day. Your ratings and history are untouched.
          </p>
        </div>
      ) : null}

      <Card className="mb-4">
        <p className="m-0 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
          Outstanding
        </p>
        <p className="m-0 mt-1 font-display text-[32px] font-bold tabular-nums text-ink">
          {data.statement.outstanding_display}
        </p>
        <p className="m-0 mt-2 max-w-[62ch] text-[13px] leading-relaxed text-muted">{data.note}</p>
      </Card>

      {!clear && data.howToPay ? (
        <Card className="mb-4">
          <h2 className="m-0 mb-2.5 font-display text-[15px] font-bold">How to clear it</h2>
          <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-2 text-[13px] sm:grid-cols-2">
            <Row label="Bank" value={data.howToPay.bank_name} />
            <Row label="Account name" value={data.howToPay.account_name} />
            <Row label="Account number" value={data.howToPay.account_number} mono />
            <Row label="Use as reference" value={data.howToPay.use_reference} mono />
          </dl>
          <p className="m-0 mt-3 text-[12.5px] leading-relaxed text-muted">
            Put the reference in the transfer narration so we can match it without ringing you.
            {data.howToPay.support_phone ? ` Any questions: ${data.howToPay.support_phone}.` : ''}
          </p>
        </Card>
      ) : null}

      {data.statement.lines.length > 0 ? (
        <Card className="p-0">
          <h2 className="m-0 px-4 pt-4 font-display text-[15px] font-bold">What this is for</h2>

          {/* Scrolls inside its own container — six columns of figures will
              never fit a 320px screen, and the page must not scroll sideways. */}
          <div className="mt-3 w-full overflow-x-auto">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr>
                  {['Raised', 'Order', 'Reason', 'Amount', 'Recovered', 'Left'].map((header, i) => (
                    <th
                      key={header}
                      className={`whitespace-nowrap border-y border-line bg-paper px-3 py-2 font-mono text-[10px] uppercase tracking-[0.1em] text-faint ${
                        i > 2 ? 'text-right' : 'text-left'
                      }`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.statement.lines.map((line, index) => (
                  <tr key={`${line.order ?? 'line'}-${index}`}>
                    <td className="whitespace-nowrap border-b border-line px-3 py-2.5 text-muted">
                      {line.raised_on ?? '—'}
                    </td>
                    <td className="whitespace-nowrap border-b border-line px-3 py-2.5 font-mono text-[11.5px] text-ink">
                      {line.order ?? '—'}
                    </td>
                    <td className="border-b border-line px-3 py-2.5 text-muted">
                      {line.reason?.replace(/_/g, ' ') ?? '—'}
                    </td>
                    <td className="whitespace-nowrap border-b border-line px-3 py-2.5 text-right font-mono tabular-nums text-ink">
                      {line.amount_display}
                    </td>
                    <td className="whitespace-nowrap border-b border-line px-3 py-2.5 text-right font-mono tabular-nums text-muted">
                      {line.recovered_display}
                    </td>
                    <td className="whitespace-nowrap border-b border-line px-3 py-2.5 text-right font-mono font-bold tabular-nums text-ink">
                      {line.outstanding_display}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-muted">{label}</dt>
      <dd className={`m-0 font-medium text-ink ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</dd>
    </div>
  );
}
